import { BookOpen, Lock, LogIn, MessageCircle, Search } from "lucide-react";
import { useRouter } from "next/router";
import React from "react";
import { useIntercom } from "react-use-intercom";
import { COMMAND_PALETTE_KEYBOARD_SHORTCUT } from "~/components/command-palette/CommandPalette";
import { useCommandPalette } from "~/components/command-palette/CommandPaletteContext";
import { NotificationFeed } from "~/components/notifications/NotificationFeed";
import { Link } from "~/components/ui/core/Link";
import { SidebarGroup, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "~/components/ui/shadcn/ui/sidebar";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { formatShortcut } from "~/lib/shadcn";

export const SidebarBottom: React.FC = () => {
  const { t } = useI18n();
  const intercom = useIntercom();
  const router = useRouter();
  const { user } = useSession();
  const { showPalette, togglePalette } = useCommandPalette();
  const featureFlags = useFeatureFlags();

  const showSearch = !!user;
  const showPrivacyPolicy = !user;
  const showNotifications = !!user && featureFlags.CAN_ACCESS_KNOCK_NOTIFICATIONS;
  const showSignIn = !user;

  const searchShortcut = formatShortcut(COMMAND_PALETTE_KEYBOARD_SHORTCUT);

  return (
    <SidebarGroup className="mt-auto">
      <SidebarMenu>
        {showSignIn && (
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t("components.layout.sidebar.sign-in")}
              isActive={router.pathname === "/sign-in"}
            >
              <Link noLinkStyle to={{ pathname: "/sign-in" }}>
                <LogIn />
                <span>{t("components.layout.sidebar.sign-in")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {showSearch && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={`${t("components.layout.sidebar.search")} (${searchShortcut})`}
              isActive={showPalette}
              onClick={() => togglePalette()}
            >
              <Search />
              <span>{t("components.layout.sidebar.search")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {showNotifications && <NotificationFeed />}

        <SidebarMenuItem>
          <SidebarMenuButton asChild tooltip={t("components.layout.sidebar.documentation")}>
            <a href="https://help.figures.hr" target="_blank" rel="noopener noreferrer">
              <BookOpen />
              <span>{t("components.layout.sidebar.documentation")}</span>
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>

        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip={t("components.layout.sidebar.contact-us")}
            onClick={() => intercom.showNewMessages()}
          >
            <MessageCircle />
            <span>{t("components.layout.sidebar.contact-us")}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>

        {showPrivacyPolicy && (
          <SidebarMenuItem>
            <SidebarMenuButton tooltip={t("components.layout.sidebar.privacy-policy")}>
              <Lock />
              <a href="https://figures.hr/other/application-privacy-policy" target="_blank" rel="noopener noreferrer">
                {t("components.layout.sidebar.privacy-policy")}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
};
